import React from 'react'
import { Select, SelectValue, SelectTrigger, SelectContent, SelectItem, Input, IconButton } from '@shopflo/ui'
import { X, ArrowLineUp } from '@phosphor-icons/react'
import { useDiscountForm } from '../../../hooks/useDiscountForm'
import {
  DiscountApplyToType,
  PrerequisiteType,
  DiscountFieldPaths,
  UiConfigDiscountType,
  PricePrerequisite,
  QuantityPrerequisite,
  PurchaseConditionAmountType,
  PurchaseConditionType,
  StandardPrerequisite
} from 'src/lib/types/discountsV2'
import FormErrorText from 'src/components/common/FormErrorText'
import { BxgyBadge } from '../../../common/ProductIcons'
import { classNames } from 'src/lib/utils/helpers'

const DiscountApplicationRulesBxgyBuyXAllProducts = () => {
  const {
    state: { prerequisite: rawPrerequisite, errors, uiConfig },
    actions: { setFormState, registerRef }
  } = useDiscountForm()

  const prerequisite = rawPrerequisite as StandardPrerequisite
  const currentType =
    prerequisite.prerequisiteType === PrerequisiteType.NONE ? PrerequisiteType.QUANTITY : prerequisite.prerequisiteType

  const handleBuyXTypeChange = (value: string) => {
    const newType = value as PrerequisiteType

    setFormState({
      prerequisite: {
        ...prerequisite,
        prerequisiteType: newType,
        purchaseConditionType:
          newType === PrerequisiteType.PRICE ? PurchaseConditionType.CART_AMOUNT : PurchaseConditionType.CART_QUANTITY
      } as StandardPrerequisite
    })
  }

  const handleRangeChange = (type: 'min' | 'max', value: string) => {
    const newVal = Number(value)
    let updatePrereqFields = {}

    if (prerequisite.prerequisiteType === PrerequisiteType.PRICE) {
      if (type === 'min') updatePrereqFields = { prerequisiteSubtotalRange: value === '' ? null : newVal }
      else updatePrereqFields = { prerequisiteMaxOrderValue: value === '' ? null : newVal }
    } else if (prerequisite.prerequisiteType === PrerequisiteType.QUANTITY) {
      if (type === 'min') updatePrereqFields = { prerequisiteCountToEntitlement: value === '' ? null : newVal }
      else updatePrereqFields = { prerequisiteMaxCountForEntitlement: value === '' ? null : newVal }
    }

    setFormState({
      prerequisite: {
        ...prerequisite,
        ...updatePrereqFields
      }
    })
  }

  const handleApplyToTypeChange = (value: string) => {
    const val = value as DiscountApplyToType

    if (val === DiscountApplyToType.ALL_PRODUCTS) return

    setFormState(
      {
        prerequisite: {
          ...prerequisite,
          purchaseConditionAmountType:
            val === DiscountApplyToType.SPECIFIC_COLLECTIONS
              ? PurchaseConditionAmountType.SPECIFIC_COLLECTIONS
              : PurchaseConditionAmountType.SPECIFIC_PRODUCTS,
          // Reset relevant fields when switching away
          prerequisiteCatalogue: [],
          metaData: {
            ...prerequisite.metaData,
            prerequisiteCatalogue: [[]]
          }
        }
      },
      [DiscountFieldPaths.PREREQUISITE_CATALOGUE]
    )
  }

  const getInputValue = () => {
    if (prerequisite.prerequisiteType === PrerequisiteType.PRICE) {
      return (prerequisite as PricePrerequisite).prerequisiteSubtotalRange ?? ''
    } else if (prerequisite.prerequisiteType === PrerequisiteType.QUANTITY) {
      return (prerequisite as QuantityPrerequisite).prerequisiteCountToEntitlement ?? ''
    }
    return ''
  }

  const getMaxValue = () => {
    if (prerequisite.prerequisiteType === PrerequisiteType.PRICE) {
      return (prerequisite as PricePrerequisite).prerequisiteMaxOrderValue
    } else if (prerequisite.prerequisiteType === PrerequisiteType.QUANTITY) {
      return (prerequisite as QuantityPrerequisite).prerequisiteMaxCountForEntitlement
    }
    return null
  }

  const maxValue = getMaxValue()
  const hasMax = maxValue !== undefined && maxValue !== null

  return (
    <div className='flex w-full pb-6'>
      <div className='flex flex-col gap-2 rounded-lg w-full'>
        <div className='flex flex-col gap-4'>
          <div>
            <div className='rounded-xl flex flex-col gap-3'>
              <div className='flex justify-between items-start'>
                <div className='flex flex-col gap-3 w-full'>
                  <div className='flex items-center gap-2 flex-wrap'>
                    <BxgyBadge label='X' />
                    <span className='text-text-sm-semibold text-zinc-800'>If shopper buys</span>

                    <div className='flex items-center gap-2'>
                      <div className='flex items-center gap-0'>
                        <Input
                          ref={node =>
                            registerRef(
                              prerequisite.prerequisiteType === PrerequisiteType.PRICE
                                ? DiscountFieldPaths.PREREQUISITE_SUBTOTAL_RANGE
                                : DiscountFieldPaths.PREREQUISITE_COUNT_TO_ENTITLEMENT,
                              node
                            )
                          }
                          type='number'
                          value={getInputValue()}
                          onChange={e => handleRangeChange('min', e.target.value)}
                          onFocus={e => e.target.select()}
                          dimension='sm'
                          variant='filled'
                          className={classNames(hasMax ? 'rounded-r-none ' : '', 'max-w-36')}
                          style={{
                            width: `${(String(getInputValue() || '').length || 1) + 2}ch`,
                            minWidth: '4ch'
                          }}
                          startAdornment={<span className='text-xs text-zinc-500 pr-1'>Min</span>}
                          endAdornment={
                            !hasMax ? (
                              <IconButton
                                IconComponent={ArrowLineUp}
                                size='xs'
                                variant='ghost'
                                onClick={() => handleRangeChange('max', '0')}
                                className='bg-transparent text-zinc-400 hover:text-zinc-600'
                              />
                            ) : undefined
                          }
                          error={
                            !!errors[
                              prerequisite.prerequisiteType === PrerequisiteType.PRICE
                                ? DiscountFieldPaths.PREREQUISITE_SUBTOTAL_RANGE
                                : DiscountFieldPaths.PREREQUISITE_COUNT_TO_ENTITLEMENT
                            ]
                          }
                          placeholder='0'
                        />

                        {hasMax && (
                          <Input
                            ref={node =>
                              registerRef(
                                prerequisite.prerequisiteType === PrerequisiteType.PRICE
                                  ? DiscountFieldPaths.PREREQUISITE_MAX_ORDER_VALUE
                                  : DiscountFieldPaths.PREREQUISITE_MAX_COUNT_FOR_ENTITLEMENT,
                                node
                              )
                            }
                            type='number'
                            value={maxValue ?? ''}
                            onChange={e => handleRangeChange('max', e.target.value)}
                            onFocus={e => e.target.select()}
                            className='rounded-l-none  max-w-36 min-w-fit'
                            dimension='sm'
                            variant='filled'
                            style={{
                              width: `${(String(maxValue ?? '').length || 1) + 2}ch`,
                              minWidth: '4ch'
                            }}
                            startAdornment={<span className='text-xs text-zinc-500 pr-1'>Upto</span>}
                            placeholder='∞'
                            endAdornment={
                              <IconButton
                                IconComponent={X}
                                size='xs'
                                variant='ghost'
                                onClick={() => handleRangeChange('max', '')}
                                className='bg-transparent text-zinc-400 hover:text-zinc-600'
                              />
                            }
                          />
                        )}
                      </div>
                      <Select value={currentType} onValueChange={handleBuyXTypeChange}>
                        <SelectTrigger
                          className='border-0 p-0 h-auto focus:ring-0 shadow-none bg-transparent hover:bg-transparent text-zinc-500 text-text-sm-regular flex gap-1 items-center w-auto'
                          showCaret
                        >
                          <span className='text-text-sm-semibold text-zinc-600 transition-colors group-hover:text-zinc-900'>
                            <SelectValue />
                          </span>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value={PrerequisiteType.QUANTITY}>quantity of</SelectItem>
                          <SelectItem value={PrerequisiteType.PRICE}>worth of</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Select value={DiscountApplyToType.ALL_PRODUCTS} onValueChange={handleApplyToTypeChange}>
                      <SelectTrigger className=' bg-white rounded-lg h-9 w-[50%]'>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={DiscountApplyToType.SPECIFIC_PRODUCTS}>specific products</SelectItem>
                        <SelectItem value={DiscountApplyToType.SPECIFIC_COLLECTIONS}>specific collections</SelectItem>
                        {uiConfig.type === UiConfigDiscountType.BXGY_AUTO_ADDED && (
                          <SelectItem value={DiscountApplyToType.ALL_PRODUCTS}>all products</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>

                  <FormErrorText
                    message={
                      errors[
                        prerequisite.prerequisiteType === PrerequisiteType.PRICE
                          ? DiscountFieldPaths.PREREQUISITE_SUBTOTAL_RANGE
                          : DiscountFieldPaths.PREREQUISITE_COUNT_TO_ENTITLEMENT
                      ]
                    }
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DiscountApplicationRulesBxgyBuyXAllProducts
